import { useState, useEffect, useCallback, useRef } from 'react';

// Reusable hook for paginated resources with loading, error, total count, and infinite-scroll
export const useInfiniteResource = ({ fetchFn }) => {
	const [data, setData] = useState([]);
	const [totalCount, setTotalCount] = useState(null); // null = not loaded yet, number = loaded count
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [error, setError] = useState(null);
	const dataRef = useRef(data);

	dataRef.current = data;

	const loadPage = useCallback(
		async (pageNumber, mode = 'initial') => {
			try {
				if (mode === 'initial') setLoading(true);
				if (mode === 'loadMore') setLoadingMore(true);
				if (mode === 'refresh') setRefreshing(true);

				setError(null);

				const response = await fetchFn(pageNumber);

				const records = response?.data ?? [];
				const total = Number(response?.totalCount ?? 0);

				setTotalCount(total);

				setData((prev) => {
					const nextData = pageNumber === 1 ? records : [...prev, ...records];

					// An empty page means the server has no more records, even
					// if totalCount over-reports — otherwise loadMore keeps
					// requesting empty pages forever
					setHasMore(records.length > 0 && nextData.length < total);

					return nextData;
				});
			} catch (err) {
				setError(err?.message ?? null);
			} finally {
				setLoading(false);
				setLoadingMore(false);
				setRefreshing(false);
			}
		},
		[fetchFn]
	);

	useEffect(() => {
		// Reset pagination when the resource (fetchFn) changes so a later
		// loadMore continues from page 2 of the new resource
		setPage(1);
		loadPage(1);
	}, [loadPage]);

	const loadMore = useCallback(() => {
		if (!loadingMore && !loading && hasMore) {
			const nextPage = page + 1;

			setPage(nextPage);
			loadPage(nextPage, 'loadMore');
		}
	}, [page, loading, loadingMore, hasMore, loadPage]);

	const refresh = useCallback(() => {
		setPage(1);

		// Intentionally do not clear data or totalCount here
		// to avoid UI flicker (e.g. alert/news-feed counts disappearing)
		return loadPage(1, 'refresh');
	}, [loadPage]);

	// Removes the first item matching `matcher` (a predicate fn) without refetching.
	// Returns { item, index } so the caller can restore it later via insertItem,
	// or null if nothing matched.
	const removeItem = useCallback((matcher) => {
		const current = dataRef.current;
		const index = current.findIndex(matcher);

		if (index === -1) return null;

		const item = current[index];
		
		// Update list and total count
		dataRef.current = current.filter((_, i) => i !== index);
		setData(dataRef.current);
		setTotalCount((prev) => (prev === null ? prev : Math.max(0, prev - 1)));

		// Used for undo action if you want element to go back to its original position
		return { item, index };
	}, []);

	// Inserts `item` at `index` (appended when omitted) without refetching.
	const insertItem = useCallback((item, index) => {
		const current = dataRef.current;
		const at = index === undefined ? current.length : Math.min(Math.max(index, 0), current.length);

		dataRef.current = [...current.slice(0, at), item, ...current.slice(at)];
		setData(dataRef.current);
		setTotalCount((prev) => (prev === null ? prev : prev + 1));
	}, []);

	return {
		data,
		removeItem,
		insertItem,
		totalCount,
		loading, // initial load
		loadingMore, // pagination
		refreshing, // manual refresh
		error,
		hasMore,
		loadMore,
		refresh,
	};
};
